import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { DoubleState, useDoubleStore } from '@/context/DoubleProvider'
import { Box } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DoubleField } from '../atoms/DoubleField'
import PointerIcon from '@/public/assets/images/double/dagger.png'
import { Image } from '@/components/HOC/Image'
import { Track, TrackController } from '../../../molecules/Track/Track'
import { DoubleColor } from '@/__generated__/graphql'

export const DoubleTrack = () => {
  const state = useDoubleStore((state) => state.state)
  const finishedTimestamp = useDoubleStore((state) => state.finishedTimestamp)
  const currentRoll = useDoubleStore((state) => state.currentRoll)
  const previousRoll = useDoubleStore((state) => state.previousRoll)

  const ref = useRef<TrackController>({} as TrackController)

  const fields = useMemo(() => getDoubleFields(), [])
  const audio = useAudio()
  const [isTrackInitialized, setTrackInitialized] = useState(false)
  const firstRender = useRef(true)
  const [isLastStage, setLastStage] = useState({ isRoundFinished: false, index: -1 })

  useEffect(() => {
    if (!isTrackInitialized) {
      return
    }

    switch (state) {
      case DoubleState.INIT:
        setLastStage({ isRoundFinished: false, index: -1 })
        useDoubleStore.setState({ isRoundFinished: false })
        break
      case DoubleState.PROCESSING:
        setLastStage({ isRoundFinished: false, index: -1 })
        useDoubleStore.setState({ isRoundFinished: false })
        break
      case DoubleState.DRAWING:
        firstRender.current = false
        setLastStage({ isRoundFinished: false, index: -1 })
        useDoubleStore.setState({ isRoundFinished: false })
        ref.current?.start()
        break
      case DoubleState.FINISHED:
        if (firstRender.current) {
          setLastStage({ isRoundFinished: false, index: -1 })
          useDoubleStore.setState({ isRoundFinished: false })
          ref.current?.startStopIndex(finishedTimestamp, currentRoll!)
        } else {
          ref.current?.stopIndex(finishedTimestamp, currentRoll!)
        }

        firstRender.current = false
        break
    }
  }, [isTrackInitialized, state])

  if (!previousRoll && previousRoll !== 0) {
    return null
  }

  return (
    <>
      <Box py={12} pb={{ base: 0, md: 12 }} h={{ base: 40, md: 48 }} pos={'relative'} overflow={'hidden'}>
        <Box position={'absolute'} left={'50%'} transform={'translateX(-50%)'}>
          <Track
            initialPosition={previousRoll}
            isTrackInitialized={isTrackInitialized}
            setTrackInitialized={() => setTrackInitialized(true)}
            ref={ref}
            width={1530}
            elements={fields}
            renderElement={({ field, index }) => (
              <Box px={'3px'}>
                <DoubleField
                  isDisabled={isLastStage.isRoundFinished ? index !== isLastStage.index : false}
                  type={field}
                />
              </Box>
            )}
            elementWidth={102}
            roundFinishedCallback={(index) => {
              audio.play(SOUNDS.PLAYER_WON)
              useDoubleStore.setState({ isRoundFinished: true })
              setLastStage({ isRoundFinished: true, index })
            }}
            panelChangeCallback={() => audio.play(SOUNDS.ROLLING_STEP)}
          />
        </Box>
        <Pointer />
      </Box>
    </>
  )
}

const getDoubleFields = () => {
  return Array.from({ length: 15 }, (_v, k) => {
    if (k === 0) {
      return DoubleColor.Green
    }

    return k % 2 === 0 ? DoubleColor.Black : DoubleColor.Red
  })
}

const Pointer = () => {
  const isRoundFinished = useDoubleStore((x) => x.isRoundFinished)

  const variants = {
    show: {
      opacity: 1,
    },
    hide: {
      opacity: 0,
    },
  }

  return (
    <motion.div
      key="animation-on-state"
      variants={variants}
      animate={isRoundFinished ? 'hide' : 'show'}
      transition={{ delay: 0.5 }}
    >
      <Image
        top={0}
        pos={'absolute'}
        left={'calc(50% - 3px)'}
        transform={'translateX(-50%) rotate(180deg)'}
        src={PointerIcon}
        alt={''}
      />
      <Image
        display={{ base: 'none', md: 'block' }}
        pos={'absolute'}
        bottom={0}
        left={'calc(50% + 2px)'}
        transform={'translateX(-50%)'}
        src={PointerIcon}
        alt={''}
      />
    </motion.div>
  )
}
